/* eslint-disable no-param-reassign */
// @flow
import React, { useState } from 'react';
import { useCombobox, useMultipleSelection } from 'downshift';
import classNames from 'classnames';
import { t } from 'javascripts/utils/withTranslation';
import Icon from 'components/_particles/icon/react/Icon';
import Label from 'components/atoms/label/react/Label';
import Button from 'components/atoms/button/Button';

export type Option = {
  text?: string,
  value: string,
  selected?: boolean,
  number?: string,
};

export type Props = {
  options: Option[],
  selectedItems: Option[],
  setSelectedItems: (items: Option[]) => void,
  id?: string,
  name?: string,
  placeholder?: string,
  labelText?: string,
  labelAdornment?: React.Node,
  large?: boolean,
  showActionButtons?: boolean,
};

function getFilteredItems(
  selectedItems?: Option[],
  inputValue?: string,
  options: Option[],
) {
  const lowerCasedInputValue = inputValue?.toLowerCase() || '';

  return options.filter((option) =>
    option.text?.toLowerCase().includes(lowerCasedInputValue),
  );
}

const getSelectionKey = (items: Option[] = []): string =>
  items
    .map((item) => item.value)
    .sort()
    .join('|');

const ControlledMultiselect = ({
  options,
  selectedItems,
  setSelectedItems,
  id,
  name,
  placeholder,
  labelText,
  labelAdornment,
  large,
  showActionButtons = false,
}: Props): React$MixedElement => {
  const rootRef = React.useRef<?HTMLDivElement>(null);
  const toggleButtonRef = React.useRef<?HTMLButtonElement>(null);
  const inputRef = React.useRef<?HTMLInputElement>(null);
  const selectedSummaryId = React.useId();
  const announcementId = React.useId();
  const [inputValue, setInputValue] = useState('');
  const [draftSelectedItems, setDraftSelectedItems] = React.useState<Option[]>(
    selectedItems || [],
  );
  const lastSyncedPropSelectionKeyRef = React.useRef<string>(
    getSelectionKey(selectedItems || []),
  );
  const [isKeyboardNavigation, setIsKeyboardNavigation] =
    React.useState<boolean>(false);
  const [announcement, setAnnouncement] = React.useState<string>('');
  const hasRenderedSelectionAnnouncementRef = React.useRef<boolean>(false);
  const currentSelectedItems = showActionButtons
    ? draftSelectedItems
    : selectedItems;
  const chipSelectedItems = showActionButtons
    ? selectedItems
    : currentSelectedItems;
  const appliedSelectedCount = selectedItems?.length || 0;
  const hasResettableSelection =
    currentSelectedItems.length > 0 || appliedSelectedCount > 0;

  const getSelectedItemsAnnouncement = React.useCallback(
    (itemsForAnnouncement: Option[]) => {
      const count = itemsForAnnouncement.length;
      const allOptionsSelected = options.length > 0 && count === options.length;

      if (count === 0) {
        return t('Keine Elemente ausgewählt');
      }

      if (allOptionsSelected) {
        return t('Alle Optionen ausgewählt');
      }

      if (count > 2) {
        return t(`${count} Elemente ausgewählt`);
      }

      return `${t('Ausgewählt')}: ${itemsForAnnouncement
        .map((item) => item.text || item.value)
        .join(', ')}`;
    },
    [options],
  );

  const selectedSummaryText = getSelectedItemsAnnouncement(
    chipSelectedItems || [],
  );

  React.useEffect(() => {
    if (!showActionButtons) {
      return;
    }

    const nextSelectionKey = getSelectionKey(selectedItems || []);
    if (nextSelectionKey === lastSyncedPropSelectionKeyRef.current) {
      return;
    }

    lastSyncedPropSelectionKeyRef.current = nextSelectionKey;
    setDraftSelectedItems(selectedItems || []);
  }, [selectedItems, showActionButtons]);

  const items = React.useMemo(
    () => getFilteredItems(currentSelectedItems, inputValue, options),
    [currentSelectedItems, inputValue, options],
  );

  const updateSelectedItems = (nextItems: Option[]) => {
    if (showActionButtons) {
      setDraftSelectedItems(nextItems);
      return;
    }

    setSelectedItems(nextItems);
  };

  const { getDropdownProps, removeSelectedItem } = useMultipleSelection({
    selectedItems: currentSelectedItems,
    onStateChange({ selectedItems: newSelectedItems, type }) {
      switch (type) {
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
        case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
        case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
          updateSelectedItems(newSelectedItems || []);
          break;
        default:
          break;
      }
    },
  });

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
  } = useCombobox({
    items,
    inputValue,
    selectedItem: null,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            ...(changes.selectedItem && {
              isOpen: true,
              highlightedIndex: state.highlightedIndex,
            }),
          };
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            ...(changes.selectedItem && { isOpen: true, highlightedIndex: 0 }),
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.keyDownSpaceButton:
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (
            newSelectedItem &&
            !currentSelectedItems?.includes(newSelectedItem)
          ) {
            updateSelectedItems([
              ...(currentSelectedItems || []),
              newSelectedItem,
            ]);
          } else {
            updateSelectedItems(
              currentSelectedItems?.filter(
                (item) => item !== newSelectedItem,
              ) || [],
            );
          }
          break;
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(newInputValue);
          break;
        default:
          break;
      }
    },
    onHighlightedIndexChange: ({ type }) => {
      const keyboardTypes = [
        useCombobox.stateChangeTypes.InputKeyDownArrowDown,
        useCombobox.stateChangeTypes.InputKeyDownArrowUp,
        useCombobox.stateChangeTypes.InputKeyDownHome,
        useCombobox.stateChangeTypes.InputKeyDownEnd,
      ];
      setIsKeyboardNavigation(keyboardTypes.includes(type));
    },
  });

  React.useEffect(() => {
    if (isOpen) {
      setAnnouncement(
        showActionButtons
          ? `${t(
              'Navigieren Sie mit den Pfeiltasten. Bestätigen Sie die Auswahl mit der Eingabetaste.',
            )} ${t('Klicken Sie danach auf Anwenden, um Ihre Auswahl zu übernehmen und die Suchergebnisse zu aktualisieren.')}`
          : `${t(
              'Navigieren Sie mit den Pfeiltasten. Bestätigen Sie die Auswahl mit der Eingabetaste.',
            )}`,
      );
    } else {
      setAnnouncement('');
    }
  }, [isOpen, showActionButtons]);

  React.useEffect(() => {
    if (!hasRenderedSelectionAnnouncementRef.current) {
      hasRenderedSelectionAnnouncementRef.current = true;
      return;
    }

    setAnnouncement(getSelectedItemsAnnouncement(currentSelectedItems || []));
  }, [currentSelectedItems, getSelectedItemsAnnouncement]);

  React.useEffect(() => {
    const $input = inputRef.current;
    if (!$input) {
      return undefined;
    }

    const handleInputBlur = () => {
      $input.classList.remove('focus-within');
    };

    $input.addEventListener('blur', handleInputBlur);

    return () => {
      $input.removeEventListener('blur', handleInputBlur);
    };
  }, []);

  const renderSelectedItem = (selectedItemForRender: Option, index: number) => (
    <span
      key={`selected-item-${selectedItemForRender.value}${index}`}
      className="multiselect__chip"
    >
      {selectedItemForRender.text}
    </span>
  );

  const handleToggle = (item: Option) => {
    if (currentSelectedItems?.map((i) => i.value).includes(item.value)) {
      removeSelectedItem(item);
    } else {
      updateSelectedItems([...(currentSelectedItems || []), item]);
    }
  };

  const handleResetSelection = () => {
    if (showActionButtons) {
      setDraftSelectedItems([]);
      setSelectedItems([]);
      return;
    }
    setSelectedItems([]);
  };

  const handleSubmitSelection = () => {
    if (!showActionButtons) {
      return;
    }

    setSelectedItems(draftSelectedItems);
    closeMenu();

    window.requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const handleInputKeyDown = (event: KeyboardEvent) => {
    if (event.key === ' ' && !isOpen) {
      event.preventDefault();
      if (toggleButtonRef.current) {
        toggleButtonRef.current.click();
      }
    }

    if (event.key === 'Enter' && !isOpen) {
      event.preventDefault();
      if (highlightedIndex === -1) {
        // $FlowFixMe
        (event.nativeEvent: any).preventDownshiftDefault = true;
        openMenu();
      }
    }
  };

  const getInputPlaceholder = () => {
    if (isOpen) {
      return t('Auswählen');
    }
    if (showActionButtons) {
      return placeholder;
    }
    if (currentSelectedItems?.length === 0) {
      return placeholder;
    }
    return undefined;
  };

  return (
    <div
      ref={rootRef}
      className={classNames(
        'multiselect multiselect--init',
        large && 'multiselect--large',
        showActionButtons && 'multiselect--with-action-buttons',
      )}
    >
      <Label {...getLabelProps()}>
        {labelText} {labelAdornment}
      </Label>
      <div
        className={classNames(
          'multiselect__grid',
          showActionButtons &&
            appliedSelectedCount > 0 &&
            !isOpen &&
            'multiselect__grid--with-indicator',
        )}
      >
        <div
          className={classNames(
            'multiselect__chips',
            (chipSelectedItems.length === 0 || !chipSelectedItems) &&
              'multiselect__chips--hidden',
          )}
        >
          {chipSelectedItems?.length < options?.length ? (
            <>
              <span className="u-hidden-visually">{t('Ausgewählt')}:</span>
              {chipSelectedItems?.map((item, i) => renderSelectedItem(item, i))}
            </>
          ) : (
            <span>{placeholder}</span>
          )}
        </div>

        <div className="multiselect__input-wrap">
          <span id={selectedSummaryId} className="u-hidden-visually">
            {selectedSummaryText}
          </span>
          <input
            placeholder={getInputPlaceholder()}
            className={classNames(
              'select multiselect__select',
              large && 'select--large',
            )}
            aria-describedby={selectedSummaryId}
            readOnly
            {...getInputProps(
              getDropdownProps({
                preventKeyAction: isOpen,
                onKeyDown: handleInputKeyDown,
                ref: inputRef,
              }),
            )}
          />
          {showActionButtons && appliedSelectedCount > 0 && !isOpen ? (
            <button
              type="button"
              className="button button--icon multiselect__selection-indicator"
              onClick={handleResetSelection}
              aria-label={t('Zurücksetzen')}
            >
              <span>{appliedSelectedCount}</span>
              <Icon icon="bw-cross-large-line" />
            </button>
          ) : null}
          <button
            ref={toggleButtonRef}
            aria-label={t('Menü öffnen und schließen')}
            className="button button--icon multiselect__toggle"
            type="button"
            {...getToggleButtonProps()}
          >
            <Icon icon="arrow-down" />
          </button>
        </div>
      </div>

      <div
        className={classNames(
          'multiselect__menu',
          'dropdown-menu',
          isOpen && 'multiselect__dropdown--open',
          !isOpen && 'hidden',
        )}
        {...getMenuProps()}
      >
        <ul className={classNames('dropdown-menu__list')}>
          {isOpen &&
            items.map((item, index) => (
              <li
                className={classNames(
                  'dropdown-menu__list-item',
                  'multiselect__item',
                  highlightedIndex === index &&
                    'dropdown-menu__list-item--active',
                  highlightedIndex === index &&
                    isKeyboardNavigation &&
                    'multiselect__item--keyboard-focus',
                  highlightedIndex === index &&
                    !isKeyboardNavigation &&
                    'multiselect__item--hover',
                )}
                key={`${item.value}${index}`}
                {...getItemProps({
                  item,
                  index,
                })}
              >
                <div
                  className={classNames(
                    'dropdown-menu__link',
                    highlightedIndex === index && 'current',
                  )}
                >
                  <span className="dropdown-menu__link">
                    <span className="dropdown-menu__text">
                      <div
                        className={classNames(
                          'checkbox',
                          currentSelectedItems
                            .map((i) => i.value)
                            .includes(item.value) && 'checkbox--checked',
                        )}
                      >
                        <span className="checkbox__indicator">
                          <Icon
                            icon="check"
                            className="checkbox__indicator-icon"
                          />
                          <span className="u-hidden-visually">
                            {currentSelectedItems
                              .map((i) => i.value)
                              .includes(item.value) && 'checkbox--checked'
                              ? t('Ausgewählt')
                              : ''}
                          </span>
                        </span>
                        <span className="checkbox__label">
                          <span>{item.text || ''}</span>
                          {item.number && <span> ({item.number})</span>}
                        </span>
                      </div>
                    </span>
                  </span>
                </div>
              </li>
            ))}
        </ul>

        {showActionButtons && isOpen ? (
          <div
            className="multiselect__actions"
            onMouseDown={(event) => event.preventDefault()}
          >
            <Button
              type="button"
              style="secondary"
              className="multiselect__actions-reset"
              onClick={handleResetSelection}
              disabled={!hasResettableSelection}
            >
              {t('Zurücksetzen')}
            </Button>
            <Button
              type="button"
              style="primary"
              className="multiselect__actions-submit"
              onClick={handleSubmitSelection}
            >
              {t('Anwenden')}
            </Button>
          </div>
        ) : null}
      </div>

      <div
        id={announcementId}
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="u-hidden-visually"
      >
        {announcement}
      </div>
    </div>
  );
};

export default ControlledMultiselect;
